import { ApolloClient, createHttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from "@apollo/client/link/error";
import { createUploadLink } from 'apollo-upload-client';
import jwtDecode from 'jwt-decode';
import moment from 'moment';
import Cookies from 'universal-cookie';
import { ACCESS_TOKEN_COOKIE, REFRESH_TOKEN_COOKIE, removeTokenCookies, setTokenCookie } from './shared/helpers/set-token-cookie.helper';
import { LOGOUT } from './shared/graphql/auth/auth.gql';

const cookies = new Cookies();

const authUrlLink = createHttpLink({
    uri: process.env.REACT_APP_AUTH_API,
});

const apiUrlLink = createUploadLink({
    uri: process.env.REACT_APP_API,
});

const authLink = setContext(async (_, { headers }) => {
    // get the authentication token from cookies if it exists
    let token = cookies.get(ACCESS_TOKEN_COOKIE);
    const refreshToken = cookies.get(REFRESH_TOKEN_COOKIE);
    // Attempt to refresh token when remaining expiry time less than 1 minute.
    if (token && refreshToken) {
        const { exp }: any = jwtDecode(token);
        const expiryTime = moment.unix(exp);
        if (expiryTime.diff(moment(), "minutes") < 1) {
            //refresh token
            token = await generateNewToken(refreshToken);
        }
    } else if (refreshToken) {
        token = await generateNewToken(refreshToken);
    }

    // return the headers to the context so httpLink can read them
    if (token) {
        headers = {
            ...headers,
            Authorization: `Bearer ${token}`,
            'Apollo-Require-Preflight': true
        }
    }

    return { headers }
});

const generateNewToken = async (refreshToken: string) => {
    if (process.env.REACT_APP_AUTH_API) {
        const res = await fetch(process.env.REACT_APP_AUTH_API, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${refreshToken}`

            },
            body: JSON.stringify({
                query: `
                    mutation RefreshAccessToken{
                        refreshAccessToken{
                            a
                        }
                    }
                `})
        });

        const result = await res.json();
        const newAccessToken = (result.data && result.data.refreshAccessToken) ?
            result.data.refreshAccessToken.a : null;
        if (newAccessToken) {
            setTokenCookie(ACCESS_TOKEN_COOKIE, newAccessToken);
            return newAccessToken;
        } else {
            // Refresh failed: the session is gone, drop both tokens.
            removeTokenCookies();
        }
    }
    return null;
}

const SIGN_OUT_TIMEOUT_MS = 3000;

/**
 * Ends the server-side login session so the refresh token stops working.
 * Must be awaited BEFORE the token cookies are removed, because the Authorization
 * header is added by the async authLink from the cookies. Never throws and never
 * waits longer than SIGN_OUT_TIMEOUT_MS.
 */
export const serverSignOut = async (): Promise<void> => {
    try {
        await Promise.race([
            authClient.mutate({ mutation: LOGOUT, errorPolicy: 'ignore', fetchPolicy: 'no-cache' }),
            new Promise((resolve) => setTimeout(resolve, SIGN_OUT_TIMEOUT_MS)),
        ]);
    } catch {
        // ignore: logout must always proceed locally
    }
};

const handleLogout = () => {
    // Clear cookies (same domain/path as when they were set, otherwise they are not removed)
    removeTokenCookies();
    client.resetStore();
    authClient.resetStore();

    // Redirect to login page
    window.location.href = '/login';
};

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {

    if (graphQLErrors) {
        graphQLErrors.map(({ message, locations, path }) => {
            console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);

            // Check for unauthorized or invalid token error message
            if (message.includes("INVALID_TOKEN")) {
                handleLogout(); // Trigger logout if the token is invalid
            }
        });
    }

    if (networkError) console.log(`[Network error]: ${networkError}`);
});

export const client = new ApolloClient({
    link: authLink.concat(errorLink).concat(apiUrlLink),
    cache: new InMemoryCache({
    }),
});

export const authClient = new ApolloClient({
    link: authLink.concat(errorLink).concat(authUrlLink),
    cache: new InMemoryCache(),
});